import React from 'react'

import type { UploadProps } from '@fourthwall/components'
import {
  Fieldset,
  Input,
  InputUI,
  Loading,
  Select,
  Textarea,
  Toasts,
  Upload,
} from '@fourthwall/components'
import { RemovableUpload } from '@modules/Dashboard/Memberships/components'
import { getRejectionErrors } from '@utils/dropzone'

import { getCategorySelectOptions } from '../../../Podcast/utils'
import { useNewPodcastModalContext } from '../../hooks/useNewPodcastModalContext'
import * as S from './NewPodcastFields.styled'

export const NewPodcastFields = () => {
  const { newPodcastForm, isLoading, addArtwork, removeArtwork } = useNewPodcastModalContext()

  const handleFileDropRejected: UploadProps['onFileDropRejected'] = (rejectedFiles) => {
    getRejectionErrors(rejectedFiles, {
      'file-too-large': 'File is larger than 10 megabytes',
    }).forEach((rejection) => {
      Toasts.notify(rejection)
    })
  }

  const renderArtwork = () => {
    if (newPodcastForm.values.artworkUrl) {
      return (
        <S.ArtworkContainer>
          <S.UploadContainer>
            <RemovableUpload onRemove={removeArtwork}>
              <S.Artwork src={newPodcastForm.values.artworkUrl} />
            </RemovableUpload>
          </S.UploadContainer>
          <S.ArtworkInfo>
            <span>Cover artwork</span>
            <S.ArtworkRecommendation>Recommended 3000x3000px size</S.ArtworkRecommendation>
          </S.ArtworkInfo>
        </S.ArtworkContainer>
      )
    }

    return (
      <S.ArtworkContainer>
        <S.UploadContainer>
          <Loading isLoading={isLoading}>
            <Upload
              accept={{
                'image/*': [],
              }}
              maxSize={10485760}
              title="Drop image or browse"
              disabled={isLoading}
              onFileDrop={(files) => addArtwork([files[0]])}
              onFileDropRejected={handleFileDropRejected}
            />
          </Loading>
        </S.UploadContainer>
        <S.ArtworkInfo>
          <span>Cover artwork</span>
          <S.ArtworkRecommendation>Recommended 3000x3000px size</S.ArtworkRecommendation>
        </S.ArtworkInfo>
      </S.ArtworkContainer>
    )
  }

  return (
    <S.Container>
      <Fieldset>
        <Fieldset.Row>
          <Fieldset.Item>{renderArtwork()}</Fieldset.Item>
        </Fieldset.Row>
        {newPodcastForm.errors.artworkUrl && (
          <InputUI.Error>{newPodcastForm.errors.artworkUrl}</InputUI.Error>
        )}
        <Fieldset.Row>
          <Fieldset.Item>
            <Input
              label="Podcast title"
              disabled={isLoading}
              {...newPodcastForm.getFieldProps('title')}
            />
          </Fieldset.Item>
        </Fieldset.Row>
        <Fieldset.Row>
          <Fieldset.Item>
            <Textarea
              label="Podcast description"
              placeholder="Podcast description"
              disabled={isLoading}
              resize
              {...newPodcastForm.getFieldProps('description')}
            />
          </Fieldset.Item>
        </Fieldset.Row>
        <Fieldset.Row>
          <Fieldset.Item>
            <Input
              label="Author name"
              suggestion="This name will appear in the podcast players."
              disabled={isLoading}
              {...newPodcastForm.getFieldProps('authorName')}
            />
          </Fieldset.Item>
        </Fieldset.Row>
        <Fieldset.Row>
          <Fieldset.Item>
            <Input
              label="Contact email"
              suggestion="Used to claim your podcast on Spotify. Not publicly shared"
              disabled={isLoading}
              {...newPodcastForm.getFieldProps('contactEmail')}
            />
          </Fieldset.Item>
        </Fieldset.Row>
        <Fieldset.Row>
          <Fieldset.Item>
            <Select
              label="Category"
              disabled={isLoading}
              options={getCategorySelectOptions()}
              {...newPodcastForm.getFieldProps('category')}
            />
          </Fieldset.Item>
        </Fieldset.Row>
      </Fieldset>
    </S.Container>
  )
}
